import { Duration } from "luxon";

import type { TelemetryContextProvider, Transport } from "@storyboard/web-client";
import { createTransport } from "@storyboard/web-client";
import { activeTestHarness } from "./init/testHarness";
import { setupAmpli } from "./setupAmpli";
import { latestTag, parseBuildTags, releaseNumberOf, WEB_RC_PREFIX, WEB_RELEASE_PREFIX } from "./utils/buildTags";

export type ConfigData = {
  baseDomain: string;
  tusUrl: string;
  graphqlUrl: string;
  graphqlApiKey: string;
  sessionRecordingLength: number;
  gaKey: string;
  env: string;
  /** SST deploy stage (`VITE_STAGE`); absent for builds that don't come from a deploy, such as local dev. */
  stage?: string;
  trackingKey?: string;
  debugMode?: boolean;
  buildVersion?: string;

  buildSha?: string;
  buildRef?: string;
  buildTags?: string[];
  buildAuthor?: string;
  buildDate?: string;
  buildMessage?: string;
  webRc?: string;
  webRelease?: string;
  releaseTag?: number;
  /** Deploy identifier shared with Sentry (`VITE_SENTRY_RELEASE`); populates `app.release`. */
  release?: string;
};

export type FlagData = {
  debugMode?: boolean;
  recordingDebug?: boolean;
  allowInboundCalls?: boolean;
  limitedMemberRole?: boolean;
  showDebugMenu?: boolean;
  allowLocalUserSettings?: boolean;
  vadThreshold?: number;
  readOnlyMode?: boolean;
  disallowElectronUpgrade?: boolean;
  toolsTab?: boolean;
  handsFreePlayback?: boolean;
  browserCantGetToken?: boolean;
  boostCallAudio?: boolean;
  clearAllUnread?: boolean;
  inActiveTelemetry?: boolean;
  /** Per-category telemetry head-sample rates in [0, 1]; see `DEFAULT_SAMPLE_RATES`. */
  telemetrySampleRates?: Record<string, number>;
  /** LaunchDarkly `web-bootstrap-indicator`: shows the left-rail boot indicator and the cold-boot content dim. */
  webBootstrapIndicator?: boolean;
  unreadDebounceMs?: number;

  [key: string]: unknown;
};

const commitRef = import.meta?.env?.VITE_VERCEL_GIT_COMMIT_REF ?? "commit-ref-not-available";
const commitShaw = import.meta?.env?.VITE_VERCEL_GIT_COMMIT_SHA ?? "commit-shaw-not-available";
const branchUrl = import.meta?.env?.VITE_VERCEL_BRANCH_URL ?? "branch-url-not-available";
const pullRequestId = import.meta?.env?.VITE_VERCEL_GIT_PULL_REQUEST_ID ?? "pull-request-id-not-available";
const buildVersion = `${commitRef}|${commitShaw}|${branchUrl}|${pullRequestId}`;

const parsedTags = parseBuildTags(import.meta.env.VITE_BUILD_TAGS ?? "");
const latestWebRcTag = latestTag(parsedTags, WEB_RC_PREFIX);
const latestWebReleaseTag = latestTag(parsedTags, WEB_RELEASE_PREFIX);
const latestTagNumberOnly = releaseNumberOf(
  parsedTags,
  import.meta.env.VITE_NEAREST_WEB_RELEASE_TAG ?? "",
  import.meta.env.VITE_NEAREST_WEB_RC_TAG ?? "",
);

export const config: ConfigData = {
  baseDomain: import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_BASE_DOMAIN,
  graphqlUrl: import.meta.env.VITE_APPSYNC_API_URL || import.meta.env.VITE_GRAPHQL_URL,
  graphqlApiKey: import.meta.env.VITE_APPSYNC_API_KEY || import.meta.env.VITE_GRAPHQL_API_KEY,
  tusUrl: import.meta.env.VITE_TUS_URL,
  sessionRecordingLength: Number(import.meta.env.VITE_SESSION_RECORDING_LENGTH),
  gaKey: import.meta.env.VITE_GA_KEY,
  env: import.meta.env.MODE,
  stage: import.meta.env.VITE_STAGE,
  trackingKey: import.meta.env.VITE_TRACKING_KEY ?? "",
  debugMode: import.meta.env.VITE_DEBUG_MODE === "true" || import.meta.env.VITE_DEBUG === "true",
  buildVersion,
  buildSha: import.meta.env.VITE_BUILD_SHA || import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA,
  buildRef: import.meta.env.VITE_VERCEL_GIT_COMMIT_REF,
  buildAuthor: import.meta.env.VITE_VERCEL_GIT_COMMIT_AUTHOR_NAME,
  buildMessage: import.meta.env.VITE_VERCEL_GIT_COMMIT_MESSAGE,
  buildTags: parsedTags,
  buildDate: import.meta.env.VITE_BUILD_DATE,
  webRc: latestWebRcTag,
  webRelease: latestWebReleaseTag,
  releaseTag: latestTagNumberOnly,
  release: import.meta.env.VITE_SENTRY_RELEASE,
};

/**
 * Every stage except production, including local dev; gates diagnostics that would be prod noise.
 *
 * Mirrors `isProductionStage` from `infra/src/util/stage.ts` — `prod` and any `prod-<variant>` — which the web package
 * cannot import.
 */
export const isNonProdStage = config.stage !== "prod" && !config.stage?.startsWith("prod-");

const debugFromUrl = new URLSearchParams(window.location.search).get("debug");

if (debugFromUrl === "true") {
  config.debugMode = true;
} else if (debugFromUrl === "false") {
  config.debugMode = false;
}

export const flags: FlagData = {
  debugMode: config.debugMode,
  recordingDebug: false,
  allowInboundCalls: config.debugMode,
  unreadDebounceMs: Duration.fromObject({ seconds: 2 }).toMillis(),
};

type FlagChangeListener = (flags: FlagData) => void;
const flagChangeListeners: FlagChangeListener[] = [];

export function onFlagsChanged(listener: FlagChangeListener): () => void {
  flagChangeListeners.push(listener);
  return () => {
    const idx = flagChangeListeners.indexOf(listener);
    if (idx >= 0) flagChangeListeners.splice(idx, 1);
  };
}

export function notifyFlagChange(): void {
  for (const listener of flagChangeListeners) {
    listener(flags);
  }
}

export const ampli = setupAmpli(config.trackingKey);

export function login(): void {
  const dataLayer = typeof (window as any).dataLayer !== "undefined" ? (window as any).dataLayer : null;
  if (window && dataLayer) {
    dataLayer.push({
      event: {
        name: "authScreen",
        value: "Auth0 App: SMS",
      },
    });
  }
  ampli.authScreen({ "Auth0 App": "SMS" });
  const origin = window.location.origin;
  const appPath = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
  const returnTo = `${origin}/authSuccess?returnTo=${appPath}`;
  window.location.href = `${config.baseDomain}/auth/login?returnTo=${returnTo}`;
}

export function logout(returnTo?: string): void {
  ampli.client.setUserId(null);
  window.location.href = logoutLink(returnTo);
}

export function logoutLink(returnTo?: string): string {
  return `${config.baseDomain}/auth/logout${returnTo ? `?returnTo=${returnTo}` : ""}`;
}

// API-layer spans need boot context (cold/warm/incremental) main.tsx registers after telemetry starts.
let telemetryContext: TelemetryContextProvider | undefined;

export function setApiTelemetryContext(provider: TelemetryContextProvider): void {
  telemetryContext = provider;
}

// Browser-test seam: harness-enabled builds route every REST call through the injected fake instead of the network.
export const transport: Transport =
  activeTestHarness()?.transport ??
  createTransport({
    baseUrl: config.baseDomain,
    getAuthHeaders: () => Promise.resolve({}),
    onUnauthorized: login,
    telemetry: { context: () => telemetryContext?.() ?? {} },
  });
